<template>
  <div class="user_info">
    <div class="content">
      <div class="avatar-container">
        <div class="avatar">
          <span class="avatar-text">{{ avatarText }}</span>
        </div>
      </div>
      
      <div class="user-details">
        <div class="username-container">
          <div v-if="!isEditingUsername" class="username-display">
            <h3 class="username">{{ displayName }}</h3>
            <el-tooltip content="修改用户名" placement="top" effect="light">
              <i class="el-icon-edit username-edit-icon" @click="startEditUsername"></i>
            </el-tooltip>
          </div>
          <div v-else class="username-edit">
            <el-input
              ref="usernameInput"
              v-model="editingUsername"
              size="small"
              class="username-input"
              maxlength="20"
            ></el-input>
            <div class="edit-actions">
              <el-button 
                type="primary" 
                size="mini" 
                icon="el-icon-check"
                @click="confirmEditUsername"
                circle
                class="confirm-btn"
              ></el-button>
              <el-button 
                size="mini" 
                icon="el-icon-close"
                @click="cancelEditUsername"
                circle
                class="cancel-btn"
              ></el-button>
            </div>
          </div>
        </div>
        <p class="email">{{ userEmail }}</p>
      </div>
      
      <div class="actions">
        <button class="btn btn-secondary" @click="handleLogout">
          <i class="icon-logout">🚪</i>
          注销账户
        </button>
        <button class="btn btn-primary" @click="handleSignOut">
          <i class="icon-signout">👋</i>
          退出登录
        </button>
      </div>
    </div>

    <el-dialog
      title="⚠️ 确认注销账户"
      :visible.sync="showDeregisterDialog"
      width="400px"  @close="closeDeregisterDialog"
      :close-on-click-modal="false"
      center
      append-to-body
      class="deregister-dialog"
    >
      <div class="warning-box">
        <i class="el-icon-warning"></i>
        <span class="warning-text">
        账号注销将删除您的所有数据，请谨慎操作！
        </span>
      </div>
      
      <el-form 
        :model="deregisterForm" 
        :rules="deregisterRules" 
        ref="deregisterForm"
        @submit.native.prevent
      >
        <el-form-item prop="password" class="password-input-item"> 
          <el-input
            v-model="deregisterForm.password"
            type="password"
            placeholder="请输入您的登录密码进行验证"
            autocomplete="off"
            @keyup.enter.native="submitDeregisterForm"
            prefix-icon="el-icon-lock"  ></el-input>
        </el-form-item>
      </el-form>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="showDeregisterDialog = false" size="medium">取消</el-button>
        <el-button 
          type="danger" 
          @click="submitDeregisterForm"
          :loading="isSubmittingDeregister"
          size="medium"
        >
          确认
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { user_logout_path, user_update_name_path } from '@/js/api_paths';
export default {
  name: 'UserInfo',
  data() {
    // 定义表单验证规则
    const validatePassword = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请输入密码'));
      } else {
        callback();
      }
    };

    return {
      isEditingUsername: false,
      editingUsername: '',
      isSubmitting: false,
      
      // 新增：注销表单相关状态
      showDeregisterDialog: false, // 控制对话框显示
      isSubmittingDeregister: false, // 控制注销按钮加载状态
      deregisterForm: {
        password: '', // 用于收集用户密码
      },
      deregisterRules: {
        password: [
          { validator: validatePassword, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    // 从 Vuex 或 localStorage 获取用户信息
    username() {
      return this.$store?.state.username || localStorage.getItem('username') || '用户';
    },
    
    userEmail() {
      return this.$store?.state.email || localStorage.getItem('userEmail') || 'user@example.com';
    },
    
    // 显示用户名（如果过长则截断）
    displayName() {
      const name = this.username;
      return name && name.length > 8 ? name.substring(0, 8) + '..' : name;
    },
    
    // 头像显示文字（取用户名首字母）
    avatarText() {
      return this.username ? this.username.charAt(0).toUpperCase() : 'U';
    }
  },
  
  methods: {
    startEditUsername() {
      this.isEditingUsername = true;
      this.editingUsername = this.username;
      this.$nextTick(() => {
        this.$refs.usernameInput.focus();
      });
    },
    
    async confirmEditUsername() {
      // ... (用户名编辑逻辑不变) ...
      if (this.isSubmitting) {
        return;
      }
      
      this.isSubmitting = true;
      
      try {
        // 验证用户名
        if (this.editingUsername.trim() === '') {
          this.$message({
            type: 'warning',
            message: '用户名不能为空',
            duration: 1500
          });
          return;
        }
        
        if (this.editingUsername.length < 2 || this.editingUsername.length > 20) {
          this.$message({
            type: 'warning',
            message: '用户名长度必须在2-20个字符之间',
            duration: 1500
          });
          return;
        }
        
        // 如果用户名没有改变，直接取消编辑
        if (this.editingUsername === this.username) {
          this.cancelEditUsername();
          return;
        }
        
        const response = await fetch(user_update_name_path, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: this.editingUsername
          })
        });
        
        const data = await response.json();
        
        if (data.status === "success") {
          // 更新本地存储和 Vuex 状态
          localStorage.setItem('username', this.editingUsername);
          if (this.$store) {
            this.$store.commit('SET_USERNAME', this.editingUsername);
          }
          
          this.isEditingUsername = false;
          
          this.$message({
            type: 'success',
            message: '用户名更新成功',
            duration: 1500
          });
        } else {
          this.$message({
            type: 'error',
            message: data.msg || '用户名更新失败',
            duration: 1500
          });
        }
      } catch (error) {
        console.error('更新用户名失败:', error);
        this.$message({
          type: 'error',
          message: '网络错误，请稍后重试',
          duration: 1500
        });
      } finally {
        this.isSubmitting = false;
      }
    },
    
    cancelEditUsername() {
      this.isEditingUsername = false;
      this.editingUsername = '';
    },
    
    /**
     * 第一步：处理注销点击，弹出确认框，确认后显示密码输入对话框
     */
    async handleLogout() {
      try {
        // 第一次确认：告知用户操作的严重性
        await this.$confirm('该操作将永久删除您账号的所有记录，数据不可恢复！', '⚠️ 确认注销',{
          confirmButtonText: '继续注销',
          cancelButtonText: '取消',
          type: 'error', 
        });
        
        // 确认继续后，将状态设为 true，弹出表单
        this.showDeregisterDialog = true; 
        this.deregisterForm.password = ''; // 清空上次输入

        this.$nextTick(() => {
            // 注意：这里需要确保您的输入框有 ref，例如：
            // <el-input ref="passwordInput" ... />
            // 如果输入框没有 ref，您可以尝试聚焦整个表单或者对话框内部元素
            this.$refs.deregisterForm.fields[0].$el.querySelector('input').focus();
        });
        
      } catch (error) {
        // 如果用户在第一次确认框中点取消，则不做任何操作
        this.$message({
          type: 'info',
          message: '已取消注销',
          duration: 1000
        });
      }
    },

    /**
     * 第二步：处理密码表单提交
     */
    submitDeregisterForm() {
      this.$refs.deregisterForm.validate(async (valid) => {
        if (!valid) {
          return; // 验证失败，不提交
        }

        if (this.isSubmittingDeregister) {
          return;
        }
        this.isSubmittingDeregister = true;

        try {
          const response = await fetch(user_logout_path, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              password: this.deregisterForm.password 
            })
          });
          
          const data = await response.json();
          
          if(data.status === "success"){
            // 成功后清除本地数据并跳转
            localStorage.removeItem('token');
            localStorage.removeItem('username');
            localStorage.removeItem('userEmail');
            this.$store.commit('SET_IS_LOGIN', false);
            
            this.$router.push('/Login');
            
            this.$message({
              type: 'success',
              message: '账号已成功注销',
              duration: 1500
            });
          }
          else{
            // 处理后端返回的错误（例如：密码不正确）
            this.$message({
              type: 'error',
              message: data.msg || '注销失败，请稍后重试。',
              duration: 3000
            });
          }
          
        } catch (error) {
          console.error('注销过程发生错误:', error);
          this.$message({
            type: 'error',
            message: '网络错误，请稍后重试。',
            duration: 2000
          });
        } finally {
          this.isSubmittingDeregister = false;
          this.showDeregisterDialog = false; // 无论成功失败，关闭弹窗
        }
      });
    },

    /**
     * 关闭弹窗并重置表单
     */
    closeDeregisterDialog() {
      this.showDeregisterDialog = false;
      // 使用 nextTick 确保 dialog 隐藏后再调用 resetFields
      this.$nextTick(() => {
         if (this.$refs.deregisterForm) {
            this.$refs.deregisterForm.resetFields();
         }
      });
    },
    
    // ... (handleSignOut 保持不变)
    async handleSignOut() {
      try {
        await this.$confirm('确定要退出登录吗？', '确认退出', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
        });
        
        localStorage.removeItem('token');
        localStorage.removeItem('username');
        localStorage.removeItem('userEmail');
        this.$store.commit('SET_IS_LOGIN', false);
        this.$router.push('/Login');
        
        this.$message({
          type: 'success',
          message: '已退出登录',
          duration: 1500
        });
        
      } catch (error) {
        this.$message({
          type: 'info',
          message: '已取消退出',
          duration: 1000
        });
      }
    },
  }
}
</script>

<style scoped>
.user_info {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding: 20px;
  border-radius: 12px 12px 0 0;
  background: linear-gradient(135deg, rgba(237, 233, 254, 0.7) 0%, rgba(224, 219, 255, 0.8) 100%);
  backdrop-filter: blur(10px);
  position: relative;
}

.content {
  text-align: center;
  color: #64748b;
  width: 100%;
  max-width: 400px;
}

.avatar-container {
  display: flex;
  justify-content: center;
  margin-bottom: 25px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.avatar-container:hover {
  transform: scale(1.05);
}

.avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  font-weight: bold;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.user-details {
  margin-bottom: 30px;
}

.username-container {
  margin-bottom: 10px;
}

.username-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.username {
  margin: 0;
  color: #475569;
  font-size: 20px;
  font-weight: 600;
}

.username-edit-icon {
  font-size: 18px;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 4px;
  border-radius: 4px;
}

.username-edit-icon:hover {
  color: #667eea;
  background-color: rgba(102, 126, 234, 0.1);
  transform: scale(1.1);
}

.username-edit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.username-input {
  width: 180px;
}

.username-input ::v-deep .el-input__inner {
  height: 32px;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
}

.edit-actions {
  display: flex;
  gap: 4px;
}

.confirm-btn {
  background: linear-gradient(135deg, #cdb4db 0%, #a27bc0 100%) !important;
  border: none !important;
}

.confirm-btn:hover {
  background: linear-gradient(135deg, #bda4cb 0%, #926bb0 100%) !important;
  transform: translateY(-1px);
}

.cancel-btn {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0 !important;
  color: #64748b !important;
}

.cancel-btn:hover {
  background: #e2e8f0 !important;
  transform: translateY(-1px);
}

.email {
  margin: 0 0 5px 0;
  color: #64748b;
  font-size: 16px;
  word-break: break-all;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-secondary {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.btn:active {
  transform: translateY(0);
}

.btn-primary:hover {
  background: linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%);
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.icon-logout,
.icon-signout {
  font-size: 18px;
}

/* 深度选择器确保样式作用于 Element UI 内部组件 */
.deregister-dialog >>> .el-dialog__body {
  padding: 15px 30px 0px 30px; /* 调整内边距，顶部留白少一点 */
}

.deregister-dialog >>> .el-dialog__footer {
  padding: 10px 30px 20px; /* 调整页脚内边距 */
}

/* 警告框样式 */
.warning-box {
  background-color: #fef0f0; /* 柔和的警告背景色 */
  border-left: 5px solid #f56c6c; /* 左侧红色强调线 */
  padding: 15px 20px;
  margin-bottom: 25px;
  border-radius: 4px;
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
}

.warning-box .el-icon-warning {
  color: #f56c6c;
  margin-right: 10px;
  font-size: 16px;
  vertical-align: middle;
}

.warning-text strong {
  color: #f56c6c; /* 强调“永久删除” */
}

/* 密码输入框的表单项样式 */
.password-input-item {
  margin-bottom: 20px;
}

.password-input-item >>> .el-input__inner {
  border-radius: 8px; /* 圆角输入框 */
  padding-left: 35px; /* 为左侧图标留出空间 */
}

.password-input-item >>> .el-input__prefix {
  left: 8px; /* 调整图标位置 */
}

/* 页脚按钮美化 */
.dialog-footer button {
  font-weight: 600;
}

.dialog-footer .el-button--danger {
  /* 调整按钮样式 */
  background-color: #f56c6c; 
  border-color: #f56c6c;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .avatar {
    width: 80px;
    height: 80px;
    font-size: 32px;
  }
  
  .username {
    font-size: 20px;
  }
  
  .username-input {
    width: 150px;
  }
  
  .username-input ::v-deep .el-input__inner {
    font-size: 18px;
    height: 28px;
  }
  
  .username-display {
    gap: 8px;
  }
  
  .username-edit-icon {
    font-size: 16px;
  }
  
  .btn {
    padding: 10px 16px;
    font-size: 14px;
  }
}
</style>